/* prettier-ignore */

import { LOCALE_ID, NgModule, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ChunkErrorHandler } from './services/chunk-error.handler';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from 'src/app/shared/shared.module';
import { ToastrModule } from 'ngx-toastr'
import { BlurListenerModule } from './directives/blur-stats/blur-stats.module';
import { GalleryModule } from '@ks89/angular-modal-gallery';
import { ScrollCardModule } from './directives/scroll-card/scroll-card.module';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DefaultMatCalendarRangeStrategy, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { provideMarkdown, MARKED_OPTIONS } from 'ngx-markdown';
import { ScrollService } from '../chatbot/service/scrollContainer.service';
import { CookieService } from 'ngx-cookie-service';
import { ChatBotStandaloneComponent } from './shared/chat-bot-standalone/chat-bot-standalone.component';
import { Renderer, Tokenizer } from 'marked';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeZh from '@angular/common/locales/zh';
import localeJa from '@angular/common/locales/ja';
import localeRu from '@angular/common/locales/ru';
import localeNl from '@angular/common/locales/nl';
import localePt from '@angular/common/locales/pt';
import localeKo from '@angular/common/locales/ko';
import localeEu from '@angular/common/locales/eu';
import localeGl from '@angular/common/locales/gl';
import localeCa from '@angular/common/locales/ca';
// Idiomas regionales del CMS (HRS-2934). Ojo: el locale 'pt' de Angular ES el
// portugués de Brasil (por eso abajo se registra como alias de pt-BR); el
// europeo es pt-PT, que es el que usa el 'pt' del CMS (= Portugal por convención). Mantener en sincronía con REGISTERED_REGIONAL_LOCALES de
// translation.service.ts.
import localePtPT from '@angular/common/locales/pt-PT';
import localeEsAR from '@angular/common/locales/es-AR';

import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/ca';
import 'dayjs/locale/zh';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/ru';
import 'dayjs/locale/pt';
// dayjs: 'pt' es el europeo (al revés que Angular); el brasileño es 'pt-br' (HRS-2934).
import 'dayjs/locale/pt-br';
import 'dayjs/locale/gl';
import 'dayjs/locale/eu';
import 'dayjs/locale/nl';

const locales = [localeEn, localeFr, localeEs, localeDe, localeIt, localeZh, localeJa, localeRu, localeNl, localePt, localeKo, localeEu, localeGl, localeCa, localePtPT, localeEsAR];
locales.forEach(locale => registerLocaleData(locale));
// El date pipe con 'pt-BR' usa el locale 'pt' de Angular, que ya es el brasileño.
registerLocaleData(localePt, 'pt-BR');

import { eye, pinOutline, arrowBackOutline, calendarOutline, close, closeCircleOutline, addOutline, menuOutline, eyeOff, map, grid, mail, exitOutline, save, informationCircle, expandOutline, chevronBack, chevronForward } from 'ionicons/icons';
import { addIcons } from 'ionicons';
import { InstallAppComponent } from './shared/install-app/install-app.component';
import { FallbackTranslateLoader } from './fallback-translate.loader';
import { provideLottieOptions } from 'ngx-lottie';
import { IncidentsReporterButtonComponent } from './shared/incidents-reporter-button/incidents-reporter-button.component';
import { ModularSurveyComponent } from './shared/modular-survey/components';
import { LanguageInterceptor } from './interceptors';
import { AuthInterceptor } from './auth/interceptors';
import { PassportFabButtonComponent } from './shared/gamification/components/fab-button/fab-button.component';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { SwRegistrationService } from './services/sw-registration.service';

addIcons({
  'eye': eye,
  'eye-off': eyeOff,
  'pin-outline': pinOutline,
  'arrow-back-outline': arrowBackOutline,
  'calendar-outline': calendarOutline,
  'close': close,
  'close-circle-outline': closeCircleOutline,
  'add-outline': addOutline,
  'menu-outline': menuOutline,
  'map': map,
  'grid': grid,
  'mail': mail,
  'exit-outline': exitOutline,
  'save': save,
  'info': informationCircle,
  'expand-outline': expandOutline,
  'chevron-back': chevronBack,
  'chevron-forward': chevronForward
});

export function createTranslateLoader(_http: HttpClient) {
  // Embed-aware loader for every forChild scope (SharedModule, etc.). In the embedded
  // widget the remote `cms-iurban.com/langs/*.php` is cross-origin → CORS-blocked, which
  // broke runtime language changes (the chatbot stayed in the initial language). The
  // FallbackTranslateLoader loads local `/assets/i18n` in embed and keeps remote→local on
  // the same-origin site, so it's safe everywhere. (_http kept for the deps signature.)
  return new FallbackTranslateLoader();
}

// export function HttpLoaderFactory(http: HttpClient) {
//   return new TranslateHttpLoader(http);
// }

export function createFallbackTranslateLoader() {
  return new FallbackTranslateLoader();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule, 
    BrowserAnimationsModule,
    IonicModule.forRoot(),
    ToastrModule.forRoot({
      closeButton: true,
      progressBar: true,
      positionClass: 'toast-top-right',
      preventDuplicates: true, 
    }),
    // Registration strategy is provided below via SwRegistrationOptions so it
    // can be gated on the offline-route feature (see SwRegistrationService).
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.serviceWorker,
    }),
    AppRoutingModule, 
    HttpClientModule,
    // GalleryModule se mantiene a propósito: su APP_INITIALIZER conecta ModalGalleryService.open()
    // al overlay; sin él la galería del hero de detalles no abre (sin error). HRS-3045 follow-up.
    GalleryModule,
    ScrollCardModule,
    SharedModule,
    ChatBotStandaloneComponent,
    BlurListenerModule,
    InstallAppComponent,
    PassportFabButtonComponent,
    IncidentsReporterButtonComponent,
    ModularSurveyComponent,
  ],
  providers: [
    {
      provide: SwRegistrationOptions,
      useFactory: (swRegistration: SwRegistrationService): SwRegistrationOptions => ({
        enabled: environment.serviceWorker,
        // Defer registration until the offline-route feature is used.
        registrationStrategy: () => swRegistration.trigger$,
      }),
      deps: [SwRegistrationService],
    },
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createFallbackTranslateLoader,
        deps: [HttpClient],
      },
    }),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'es'},
    provideNativeDateAdapter(),
    DefaultMatCalendarRangeStrategy,
    MatRangeDateSelectionModel,
    ScrollService,
    CookieService,
    {
      provide: ErrorHandler,
      useClass: ChunkErrorHandler,
    },
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: () => {
          // GFM también interpreta ~texto~ (virgulilla simple) como tachado,
          // y nombres tipo "Derry~Londonderry" acababan renderizados con
          // <del>. Se limita el tachado a la doble virgulilla (~~texto~~).
          const tokenizer = new Tokenizer();
          const gfmDel = Tokenizer.prototype.del;
          tokenizer.del = function (src: string) {
            const token = gfmDel.call(this, src);
            return token?.raw.startsWith('~~') ? token : undefined;
          };
          const renderer = new Renderer();
          renderer.link = (href: string, title: string, text: string) => {
            const titleAttr = title ? ` title="${title}"` : '';
            return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
          };
          return {
            renderer,
            tokenizer,
            gfm: true,
            breaks: false,
            smartLists: true,
            smartypants: false,
          };
        },
      },
    }),
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
  ],
  bootstrap: [AppComponent],
})

export class AppModule {}
